import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateProfileImage, deleteProfileImage } from '../../../features/playerProfile/playerProfileSlice';
import { TeamLogoBox } from '../../../components/common/TeamLogoBox';

// Navy header for the player's own profile, laid out like the public player
// preview: photo, name, position and school on the left, team logo on the
// right. Below it, a strip showing how complete the profile is.

const ICON_CLASS = 'w-4 h-4 text-cyan-400 flex-shrink-0';

const PhoneIcon = () => (
  <svg className={ICON_CLASS} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h2.3a1 1 0 01.95.68l1.2 3.6a1 1 0 01-.27 1.05L7.6 9.9a12 12 0 006.5 6.5l1.57-1.58a1 1 0 011.05-.27l3.6 1.2a1 1 0 01.68.95V19a2 2 0 01-2 2h-1C9.72 21 3 14.28 3 6V5z" />
  </svg>
);

const EmailIcon = () => (
  <svg className={ICON_CLASS} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const InstagramIcon = () => (
  <svg className={ICON_CLASS} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const XIcon = () => (
  <svg className={ICON_CLASS} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
  </svg>
);

// Show "@handle" for a social link, falling back to the raw value
const handleFrom = (url) => {
  const match = String(url).match(/(?:x\.com|twitter\.com|instagram\.com)\/@?([^/?#]+)/i);
  return match ? `@${match[1]}` : url;
};

// Same order as the public preview: phone, email, Instagram, X. All four
// always show, with "-" when there's nothing to display. hidePrivate shows "-"
// for phone and email (another player's profile, or one not yet claimed).
export const ContactDetails = ({ profile, hidePrivate = false }) => {
  const phone = !hidePrivate && profile?.phoneNumber;
  const email = !hidePrivate && profile?.email;
  const items = [
    { label: 'Phone', icon: <PhoneIcon />, text: phone, href: phone && `tel:${phone}` },
    { label: 'Email', icon: <EmailIcon />, text: email, href: email && `mailto:${email}` },
    { label: 'Instagram', icon: <InstagramIcon />, text: profile?.instaURL && handleFrom(profile.instaURL), href: profile?.instaURL },
    { label: 'X', icon: <XIcon />, text: profile?.xURL && handleFrom(profile.xURL), href: profile?.xURL },
  ];

  return (
    <dl className="flex flex-wrap gap-x-6 gap-y-2 mt-4">
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-2 text-sm min-w-0">
          {item.icon}
          <dt className="sr-only">{item.label}</dt>
          <dd className="min-w-0">
            {item.text ? (
              <a
                href={item.href}
                target={item.label === 'Phone' || item.label === 'Email' ? undefined : '_blank'}
                rel="noopener noreferrer"
                className="text-white font-semibold hover:text-cyan-400 hover:underline truncate block"
              >
                {item.text}
              </a>
            ) : (
              <span className="text-slate-400" aria-label={`No ${item.label.toLowerCase()}`}>-</span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
};

// Headshot with upload / remove buttons on hover (or keyboard focus)
const PlayerPhoto = ({ profile, name }) => {
  const dispatch = useDispatch();
  const { profileImageLoading } = useSelector((state) => state.playerProfile);
  const fileInputRef = useRef(null);
  // A photo link that no longer loads falls back to the silhouette
  const [failedSrc, setFailedSrc] = useState(null);
  const frame = 'w-32 h-32 sm:w-44 sm:h-44 flex-shrink-0 rounded-2xl border-2 border-cyan-400/60 overflow-hidden';

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) dispatch(updateProfileImage(file));
  };

  const handleImageDelete = () => {
    if (window.confirm('Are you sure you want to delete your profile image?')) {
      dispatch(deleteProfileImage());
    }
  };

  return (
    <div className="relative group flex-shrink-0">
      {profile?.profileImage && failedSrc !== profile.profileImage ? (
        <img
          src={profile.profileImage}
          alt={name}
          onError={() => setFailedSrc(profile.profileImage)}
          className={`${frame} object-cover bg-white/10`}
        />
      ) : (
        <div className={`${frame} bg-white/10 flex items-end justify-center`} role="img" aria-label={`${name} photo`}>
          <svg className="w-4/5 h-4/5 text-white/40" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-4.42 0-8 2.69-8 6v2h16v-2c0-3.31-3.58-6-8-6z" />
          </svg>
        </div>
      )}

      {profileImageLoading ? (
        <div className="absolute inset-0 bg-black/60 rounded-2xl flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="absolute inset-0 bg-black/60 rounded-2xl opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-2 bg-cyan-400 hover:bg-cyan-300 text-slate-900 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            title="Upload new photo"
            aria-label="Upload new photo"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z" />
            </svg>
          </button>
          {profile?.profileImage && (
            <button
              type="button"
              onClick={handleImageDelete}
              className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              title="Delete photo"
              aria-label="Delete photo"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
              </svg>
            </button>
          )}
        </div>
      )}

      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
    </div>
  );
};

export const TeamLogo = ({ team }) => {
  const navigate = useNavigate();
  if (!team) return null;
  const teamId = typeof team === 'string' ? team : team._id;
  const name = typeof team === 'object' ? team.name : '';

  return (
    <TeamLogoBox
      name={name}
      logo={typeof team === 'object' ? team.logo : null}
      onClick={() => navigate(`/player/team/${teamId}`)}
      className="self-start sm:self-auto"
    />
  );
};

// Strip under the header (where the preview has "Unlock the full profile")
const ProgressBar = ({ progress, onUploadVideo, onCompleteProfile }) => {
  const radius = 18;
  const length = 2 * Math.PI * radius;

  return (
    <div className="bg-jp-section border-b border-jp-line px-4 sm:px-6 lg:px-8 py-4">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="relative w-11 h-11 flex-shrink-0">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 44 44" aria-hidden="true">
              <circle cx="22" cy="22" r={radius} stroke="currentColor" strokeWidth="5" fill="none" className="text-jp-line" />
              <circle
                cx="22"
                cy="22"
                r={radius}
                stroke="currentColor"
                strokeWidth="5"
                fill="none"
                strokeDasharray={length}
                strokeDashoffset={length * (1 - progress / 100)}
                strokeLinecap="round"
                className="text-jp-accent"
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-extrabold text-jp-ink tabular-nums">
              {progress}%
            </span>
          </div>
          <div>
            <p className="text-jp-ink font-extrabold">Your profile is {progress}% complete</p>
            <p className="text-jp-muted text-sm">Fill out your profile to 100% and be at the top.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={onUploadVideo}
            className="text-sm font-semibold text-jp-accent-ink underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent rounded"
          >
            Upload video
          </button>
          <button
            type="button"
            onClick={onCompleteProfile}
            className="px-6 py-3 text-sm font-bold uppercase tracking-wider bg-gradient-to-r from-cyan-400 via-cyan-500 to-blue-600 text-slate-900 rounded-xl hover:brightness-110 transition-all shadow-lg shadow-cyan-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent focus-visible:ring-offset-2 focus-visible:ring-offset-jp-section active:scale-[0.98]"
          >
            Complete my profile
          </button>
        </div>
      </div>
    </div>
  );
};

export const PlayerProfileHeader = ({ onCompleteProfile, onUploadVideo }) => {
  const { profile } = useSelector((state) => state.playerProfile);
  const name = profile ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim() : 'Loading...';
  const progress = profile?.profileCompleteness || profile?.profileCompletion?.percentage || 0;
  const teamName = typeof profile?.team === 'object' ? profile.team?.name : '';
  const subtitle = [profile?.primaryPosition || profile?.position, teamName].filter(Boolean).join(' · ');

  return (
    <>
      <section className="bg-jp-band px-4 sm:px-6 lg:px-8 pt-32 sm:pt-36 pb-10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div className="flex items-center gap-5 min-w-0">
            <PlayerPhoto profile={profile} name={name} />
            <div className="min-w-0">
              <p className="text-yellow-300 text-sm font-bold uppercase tracking-widest mb-2">Player Profile</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-anton leading-[1.1] tracking-tight text-white uppercase">
                {name}
              </h1>
              {/* Position and school, with the jersey number to the right of the school */}
              {(subtitle || profile?.jerseyNumber) && (
                <p className="text-slate-300 text-sm sm:text-base mt-2">
                  {subtitle}
                  {profile?.jerseyNumber && (
                    <span className="ml-2 text-white font-bold">#{profile.jerseyNumber}</span>
                  )}
                </p>
              )}
              <ContactDetails profile={profile} />
            </div>
          </div>
          <TeamLogo team={profile?.team} />
        </div>
      </section>
      {progress < 100 && (
        <ProgressBar progress={progress} onUploadVideo={onUploadVideo} onCompleteProfile={onCompleteProfile} />
      )}
    </>
  );
};

export default PlayerProfileHeader;
